<template>
    <div class="home">
        <b-container>
            <h1>Minnesota Fathers' Adoption Registry Registration Form</h1>

            <b-row class="mt-4 mb-4">
                <b-col cols="12">
                    <p>Complete this form to register with the Minnesota Fathers’ Adoption Registry (MFAR). Register before the birth or within 30 days of the child’s birth. MFAR protects the rights of putative fathers and adoptive families. You are a putative father if you:
                    </p>
                    <ul>
                        <li>think you may be the father of a child</li>
                        <li>are not married to the child’s mother</li>
                        <li>have not established paternity for the child in a court or through a voluntary acknowledgement form</li>
                        <li>want to be notified if the child is the subject of an adoption in Minnesota</li>
                    </ul>
                    <p>Registration is voluntary and available to men of any age.</p>
                    <p>Minnesota law requires a search of MFAR before finalizing an adoption. If the search of MFAR identifies the putative father, a notice must be mailed to the father’s address on file in MFAR. Minnesota Statutes, section 259.52.</p>

                </b-col>
            </b-row>

            <form-summary :validator="$v" ref="errorContainer" id="errorContainer"/>

            <b-form @submit.stop.prevent="submitForm">

                <div class="card mb-3" header-bg-variant="primary" header-text-variant="white">
                    <h2 class="card-header">Information about you - the putative father</h2>
                    <div class="card-body">
                    <p>Enter as much information as you have.</p>
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.putativeFather.name.first" 
                                    :v="$v.form.putativeFather.name.first"
                                    label="Your First Name"
                                    slug="pf-name-first"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.putativeFather.name.middle"
                                    :v="$v.form.putativeFather.name.middle"
                                    label="Your Middle Name"
                                    slug="pf-name-middle"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.putativeFather.name.last"
                                    :v="$v.form.putativeFather.name.last"
                                    label="Your Last Name"
                                    slug="pf-name-last"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                               <BaseTextInput v-model="form.putativeFather.name.suffix"
                                   :v="$v.form.putativeFather.name.suffix"
                                   label="Your Suffix Name"
                                   slug="pf-name-suffix"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.putativeFather.alias"
                                    :v="$v.form.putativeFather.alias"
                                    label="Your Alias Name"
                                    tip="(Other names you use, especially if known by the mother of the child.)"
                                    slug="pf-alias"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseDateOfBirthInput v-model="form.putativeFather.dateOfBirth"
                                    :v="$v.form.putativeFather.dateOfBirth"
                                    label="Your Date of Birth"
                                    slug="pf-dateOfBirth"
                                    :messages="dobMessages"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseMaskedTextInput v-model="form.putativeFather.ssn"
                                    :v="$v.form.putativeFather.ssn"
                                    label="Your Social Security Number"
                                    mask="###-##-####"
                                    slug="pf-ssn"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12">
                                <p style="font-size:1.2em; font-weight:500; margin-bottom: 0px;">Mailing Address</p>
                                <hr style="margin-top:0px;"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" lg="6">
                                <BaseTextInput v-model="form.putativeFather.mailingAddress.streetAddress1"
                                    :v="$v.form.putativeFather.mailingAddress.streetAddress1"
                                    label="Your Mailing Street Address #1"
                                    slug="pf-mailingAddress-streetAddress1"/>
                            </b-col>
                            <b-col cols="12" lg="6">
                                <BaseTextInput v-model="form.putativeFather.mailingAddress.streetAddress2"
                                    :v="$v.form.putativeFather.mailingAddress.streetAddress2"
                                    label="Your Mailing Street Address #2"
                                    slug="pf-mailingAddress-streetAddress2"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseTextInput v-model="form.putativeFather.mailingAddress.city"
                                    :v="$v.form.putativeFather.mailingAddress.city"
                                    label="Your Mailing City"
                                    slug="pf-mailingAddress-city"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseSelector v-model="form.putativeFather.mailingAddress.state"
                                    :v="$v.form.putativeFather.mailingAddress.state"
                                    label="Your Mailing State"
                                    :options=stateOptions
                                    slug="pf-mailingAddress-state"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseMaskedTextInput v-model="form.putativeFather.mailingAddress.postalCode"
                                    :v="$v.form.putativeFather.mailingAddress.postalCode"
                                    label="Your Mailing ZIP Code"
                                    mask="#####-####"
                                    slug="pf-mailingAddress-postalCode"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12">
                                <p style="font-size:1.2em; font-weight:500; margin-bottom: 0px;">Notification Address</p>
                                <p style="margin-bottom: 0px;">(This must be a street address to receive court notice of an adoption. If you do not have a street address, you may enter the address of someone willing to receive notice for you.)</p>
                                <hr style="margin-top:0px;"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" lg="6">
                                <BaseTextInput v-model="form.putativeFather.notificationAddress.streetAddress1"
                                    :v="$v.form.putativeFather.notificationAddress.streetAddress1"
                                    label="Your Notification Street Address #1"
                                    slug="pf-notificationAddress-streetAddress1"/>
                            </b-col>
                            <b-col cols="12" lg="6">
                                <BaseTextInput v-model="form.putativeFather.notificationAddress.streetAddress2"
                                    :v="$v.form.putativeFather.notificationAddress.streetAddress2"
                                    label="Your Notification Street Address #2"
                                    slug="pf-notificationAddress-streetAddress2"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseTextInput v-model="form.putativeFather.notificationAddress.city"
                                    :v="$v.form.putativeFather.notificationAddress.city"
                                    label="Your Notification City"
                                    slug="pf-notificationAddress-city"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseSelector v-model="form.putativeFather.notificationAddress.state"
                                    :v="$v.form.putativeFather.notificationAddress.state"
                                    label="Your Notification State"
                                    :options=stateOptions
                                    slug="pf-notificationAddress-state"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseMaskedTextInput v-model="form.putativeFather.notificationAddress.postalCode"
                                    :v="$v.form.putativeFather.notificationAddress.postalCode"
                                    label="Your Notification Zip Code"
                                    mask="#####-####"
                                    slug="pf-notificationAddress-postalCode"/>
                            </b-col>
                        </b-row>
                    </div>
                </div>

                <div class="card mb-3" header-bg-variant="primary" header-text-variant="white">
                    <h2 class="card-header">Mother's information</h2>
                    <div class="card-body">
                    <p>Enter as much information as you know.</p>
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.mother.name.first"
                                    :v="$v.form.mother.name.first"
                                    label="Mother First Name"
                                    slug="m-name-first"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.mother.name.middle"
                                    :v="$v.form.mother.name.middle"
                                    label="Mother Middle Name"
                                    slug="m-name-middle"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.mother.name.last"
                                    :v="$v.form.mother.name.last"
                                    label="Mother Last Name"
                                    slug="m-name-last"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                               <BaseTextInput v-model="form.mother.name.suffix"
                                   :v="$v.form.mother.name.suffix"
                                   label="Mother Suffix Name"
                                   slug="m-name-suffix"/>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.mother.alias"
                                    :v="$v.form.mother.alias"
                                    label="Mother Alias Name"
                                    slug="m-alias"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseDateOfBirthInput v-model="form.mother.dateOfBirth"
                                    :v="$v.form.mother.dateOfBirth"
                                    label="Mother Date of Birth"
                                    slug="m-dateOfBirth"
                                    :messages="dobMessages"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseMaskedTextInput v-model="form.mother.ssn"
                                    :v="$v.form.mother.ssn"
                                    label="Mother Social Security Number"
                                    mask="###-##-####"
                                    slug="m-ssn"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12">
                                <p style="font-size:1.2em; font-weight:500; margin-bottom: 0px;">Mailing Address</p>
                                <hr style="margin-top:0px;"/>
                            </b-col>
                        </b-row>

                        <b-row>
                             <b-col cols="12" lg="6">
                                 <BaseTextInput v-model="form.mother.mailingAddress.streetAddress1"
                                     :v="$v.form.mother.mailingAddress.streetAddress1"
                                     label="Mother Street Address #1"
                                     slug="m-mailingAddress-streetAddress1"/>
                             </b-col>
                             <b-col cols="12" lg="6">
                                 <BaseTextInput v-model="form.mother.mailingAddress.streetAddress2"
                                     :v="$v.form.mother.mailingAddress.streetAddress2"
                                     label="Mother Street Address #2"
                                     slug="m-mailingAddress-streetAddress2"/>
                             </b-col>
                             <b-col cols="12" lg="4">
                                 <BaseTextInput v-model="form.mother.mailingAddress.city"
                                     :v="$v.form.mother.mailingAddress.city"
                                     label="Mother City"
                                     slug="m-mailingAddress-city"/>
                             </b-col>
                             <b-col cols="12" lg="4">
                                 <BaseSelector v-model="form.mother.mailingAddress.state"
                                     :v="$v.form.mother.mailingAddress.state"
                                     label="Mother State"
                                     :options=stateOptions
                                     slug="m-mailingAddress-state"/>
                             </b-col>
                             <b-col cols="12" lg="4">
                                 <BaseMaskedTextInput v-model="form.mother.mailingAddress.postalCode"
                                     :v="$v.form.mother.mailingAddress.postalCode"
                                     label="Mother ZIP Code"
                                     mask="#####-####"
                                     slug="m-mailingAddress-postalCode"/>
                             </b-col>
                         </b-row>
                    </div>
                </div>

                <div class="card mb-3" header-bg-variant="primary" header-text-variant="white">
                    <h2 class="card-header">Child's Information</h2>
                    <div class="card-body">
                    <p>Enter as much information as you know.</p>
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.child.name.first"
                                    :v="$v.form.child.name.first"
                                    label="Child First Name"
                                    slug="ch-name-first"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.child.name.middle"
                                    :v="$v.form.child.name.middle"
                                    label="Child Middle Name"
                                    slug="ch-name-middle"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseTextInput v-model="form.child.name.last"
                                    :v="$v.form.child.name.last"
                                    label="Child Last Name"
                                    slug="ch-name-last"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                               <BaseTextInput v-model="form.child.name.suffix"
                                   :v="$v.form.child.name.suffix"
                                   label="Child Suffix Name"
                                   slug="ch-name-suffix"/>
                            </b-col>
                        </b-row>
                        
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseSelector v-model="form.child.gender"
                                    :v="$v.form.child.gender"
                                    label="Child Gender"
                                    :options=sexOptions
                                    slug="ch-gender"/>
                            </b-col>
                        </b-row>

                        <b-row class="mt-2 mb-2">
                            <b-col cols="12" lg="3">
                                <BaseRadioInput v-model="form.child.birthDetails.birthDateKnown"
                                    :v="$v.form.child.birthDetails.birthDateKnown"
                                    label="Is child's birth date known?"
                                    :options=trueFalseYesNoOptions
                                    slug="ch-birthDetails-birthDateKnown"/>
                            </b-col>
                            <template v-if="form.child.birthDetails.birthDateKnown">
                                <b-col cols="12" lg="3">
                                    <BaseDateOfBirthInput v-model="form.child.birthDetails.birthDate"
                                        :v="$v.form.child.birthDetails.birthDate"
                                        label="Child Date of Birth"
                                        slug="c-dateOfBirth"
                                        :messages="dobMessages"/>
                                </b-col>
                            </template>
                            <template v-else>
                                <b-col cols="12" lg="3">
                                    <BaseTextInput v-model="form.child.birthDetails.birthMonth"
                                        :v="$v.form.child.birthDetails.birthMonth"
                                        label="Birth Month (Estimate)"
                                        slug="ch-birthDetails-birthMonth"/>
                                </b-col>
                                <b-col cols="12" lg="3">
                                    <BaseTextInput v-model="form.child.birthDetails.birthYear"
                                        :v="$v.form.child.birthDetails.birthYear"
                                        label="Birth Year (Estimate)"
                                        slug="ch-birthDetails-birthYear"/>
                                </b-col>
                            </template>
                        </b-row>
                        <b-row>
                            <b-col cols="12" lg="4">
                                <BaseTextInput v-model="form.child.birthDetails.hospitalName"
                                    :v="$v.form.child.birthDetails.hospitalName"
                                    label="Place of birth (Hospital Name)"
                                    slug="ch-birthDetails-hospitalName"/>
                            </b-col>
                            <b-col cols="12" lg="4">
                                <BaseTextInput v-model="form.child.birthDetails.cityOfBirth"
                                    :v="$v.form.child.birthDetails.cityOfBirth"
                                    label="City of birth"
                                    slug="ch-birthDetails-cityOfBirth"/>
                            </b-col>
                            <b-col cols="12" lg="3">
                                <BaseSelector v-model="form.child.birthDetails.stateOfBirth"
                                    :v="$v.form.child.birthDetails.stateOfBirth"
                                    label="State of birth"
                                    :options=stateOptions
                                    slug="ch-birthDetails-stateOfBirth"/>
                            </b-col>
                        </b-row>
                    </div>
                </div>
                
                <div class="card mb-3" header-bg-variant="primary" header-text-variant="white">
                    <h2 class="card-header">Court order</h2>
                    <div class="card-body">
                        <p>Has a court in another state or territory named you the legal father of the child?</p>
                        <b-row>
                            <b-col cols="12" lg="3">
                                <BaseRadioInput v-model="form.courtOrder.courtOrderIssued"
                                    :v="$v.form.courtOrder.courtOrderIssued"
                                    label="Court order issued"
                                    :options=trueFalseYesNoOptions
                                    slug="courtOrder-courtOrderIssued"/>
                            </b-col>
                            <template v-if="form.courtOrder.courtOrderIssued">
                                <b-col cols="12" lg="4">
                                    <BaseTextInput v-model="form.courtOrder.courtFileNbr"
                                        :v="$v.form.courtOrder.courtFileNbr"
                                        label="Court file number"
                                        slug="courtOrder-courtFileNbr"/>
                                </b-col>
                                <b-col cols="12" lg="5">
                                    <label style="font-weight: 750;">Please upload a certified copy of court order</label><br/>
                                    <b-form-file v-model="file" class="mt-0" plain></b-form-file>
                                </b-col>
                            </template>
                        </b-row>
                    </div>
                </div>

                <div class="card" header-bg-variant="primary" header-text-variant="white">
                    <h2 class="card-header">Signature</h2>
                    <div class="card-body">
                        <b-row>
                            <b-col cols="12" lg="12">
                                <BaseRadioInput v-model="form.signature.statement"
                                    :v="$v.form.signature.statement"
                                    label="The information on this registration form is true and accurate to the best of my knowledge. I understand that:
                                    If I register false information on purpose, I am guilty of a crime.
                                    The information I register is private. Only those authorized to search the registry have access to my information.
                                    I must keep my address information in MFAR up-to-date so I can receive notice of an adoption."
                                    :options=yesNoOptions
                                    slug="signature-statement"
                                    :messages="signatureMessages"/>
                            </b-col>
                            <b-col cols="12" lg="6">
                                <BaseTextInput v-model="form.signature.signedName"
                                    :v="$v.form.signature.signedName"
                                    label="Type your signature to submit"
                                    slug="signature-signedName"/>
                            </b-col>
                            <b-col class="pt-4" v-if="acceptedTerms">
                                    <b-button type="submit" variant="primary">Submit</b-button>
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </b-form>

        </b-container>
    </div>
</template>

<script>
import LocalStorageService from '@/common/LocalStorageService';
import { required, email, minLength, maxLength, numeric, requiredUnless, requiredIf, ref, minValue, maxValue } from 'vuelidate/lib/validators'
import axios from 'axios';
import axiosRetry from 'axios-retry';
import moment from 'moment'
import minDate from '@/validators/minDate'
import maxDate from '@/validators/maxDate'
import { commonOptions } from '@/mixins/commonOptions.js'

export default {
  components: {
  },
  mixins: [
    commonOptions
  ],
  
  data: () => ({
      dobMessages: {
        minDate: 'Birth Date must be after 01/01/1900.',
        maxDate: 'Birth Date must not be in the future.'
      },

      signatureMessages: {
        understandStatement: 'You must agree to the statement.',
      },

      file: null, 

      form: {
        putativeFather: {
            name: {
                first: '',
                middle: '',
                last: '',
                suffix: ''
            },
            alias: '',
            dateOfBirth: null,
            ssn: '',
            mailingAddress: {
                streetAddress1: '',
                streetAddress2: '',
                city: '',
                state: 'MN',
                postalCode: ''
            },
            notificationAddress: {
                streetAddress1: '',
                streetAddress2: '',
                city: '',
                state: 'MN',
                postalCode: ''
            }
        },
        mother: {
            name: {
                first: '',
                middle: '',
                last: '',
                suffix: ''
            },
            alias: '',
            dateOfBirth: '',
            ssn: '',
            mailingAddress: {
                streetAddress1: '',
                streetAddress2: '',
                city: '',
                state: 'MN',
                postalCode: ''
            }
        },
        child: {
            name: {
                first: '',
                middle: '',
                last: '',
                suffix: ''
            },
            gender: '',
            birthDetails: {
                birthDateKnown: true,
                birthDate: null,
                birthMonth: null,
                birthYear: null,
                hospitalName: '',
                cityOfBirth: '',
                stateOfBirth: 'MN'
            }
        },
        courtOrder: {
            courtOrderIssued: false,
            courtFileNbr: '',
            fileId: null
        },
        signature: {
            statement: '',
            signedName: '',
            signedDate: ''
        }
    }
  }),
  
   validations: {           
        form: {     
            putativeFather: {
                name: {
                    first: { required,  maxLength: maxLength(50) },
                    middle: { required: false, maxLength: maxLength(50) },
                    last: { required,  maxLength: maxLength(50) },
                    suffix: { required: false,  maxLength: maxLength(10) }
                },
                alias: { required: false, maxlength: maxLength(50) },
                dateOfBirth: { required,
                    minDate: minDate(new Date("1900-01-01")),
                    maxDate: maxDate(new Date())
                },
                ssn: { required: false, maxLength: maxLength(11) },
                mailingAddress: {
                    streetAddress1: { required: false,  maxLength: maxLength(90) },
                    streetAddress2: { required: false, maxLength: maxLength(15) },
                    city: { required: false,  maxLength: maxLength(50) },
                    state: { required: false,  maxLength: maxLength(2) },
                    postalCode: { required: false,  maxLength: maxLength(10) }
                },
                notificationAddress: {
                    streetAddress1: { required,  maxLength: maxLength(90) },
                    streetAddress2: { required: false, maxLength: maxLength(15) },
                    city: { required,  maxLength: maxLength(50) },
                    state: { required,  maxLength: maxLength(2) },
                    postalCode: { required,  maxLength: maxLength(10) }
                },
            },
            mother: {
                name: {
                    first: { required: false,  maxLength: maxLength(50) },
                    middle: { required: false, maxLength: maxLength(50) },
                    last: { required: false,  maxLength: maxLength(50) },
                    suffix: { required: false,  maxLength: maxLength(10) }
                },
                alias: { required: false, maxlength: maxLength(50) },
                dateOfBirth: { required: false,
                    minDate: minDate(new Date("1900-01-01")),
                    maxDate: maxDate(new Date())
                },
                ssn: { required: false, maxLength: maxLength(11) },
                mailingAddress: {
                    streetAddress1: { required: false,  maxLength: maxLength(90) },
                    streetAddress2: { required: false, maxLength: maxLength(15) },
                    city: { required: false,  maxLength: maxLength(50) },
                    state: { required: false,  maxLength: maxLength(2) },
                    postalCode: { required: false,  maxLength: maxLength(10) }
                },
            },
            child: {
                name: {
                    first: { required: false,  maxLength: maxLength(50) },
                    middle: { required: false, maxLength: maxLength(50) },
                    last: { required: false,  maxLength: maxLength(50) },
                    suffix: { required: false,  maxLength: maxLength(10) }
                },
                gender: { required: false },
                birthDetails: {
                    birthDateKnown: { required },
                    birthDate: { required: requiredIf(function() { return this.form.child.birthDetails.birthDateKnown === true }),  minDate: minDate(new Date("1990-01-01")),
                    maxDate: maxDate(new Date()) },
                    birthMonth: { required: false, numeric, minValue: minValue(1), maxValue: maxValue(12) },
                    birthYear: { required: false, numeric, minValue: minValue(1990) },
                    hospitalName: { required: false },
                    cityOfBirth: { required: false },
                    stateOfBirth: { required: false, maxLength: maxLength(2) },
                }
            },
            courtOrder: {
                courtOrderIssued: { required: false },
                courtFileNbr: { required: requiredIf(function() { return this.form.courtOrder.courtOrderIssued === true }), maxLength: maxLength(100) },
                fileId: { required: requiredIf(function() { return this.form.courtOrder.courtOrderIssued === true }) },
            },
            signature: {
                statement: {
                required,
                understandStatement: value => value === 'Y'
                },
                signedName: { required },
                signedDate: { required }
            }
        },
    },
   

  computed: {
      acceptedTerms() {
          return this.form.signature.statement === 'Y' && this.form.signature.signedName
      }
  },

  watch: {
      file() {
          console.log("file changed")
          if (this.file) {
            this.uploadS3()
          }
      }
  },
  methods: {

    submitForm() {        
        axios.post(`${process.env.VUE_APP_API_BASE_URL}/registration`, this.form)
        .then(response => { 
            // show thank you page
            this.$router.push('thanks')
        })
    },

    uploadS3() {
        console.log("starting s3 upload")
        this.getSignedURL(this.file, '/')
    },

    getSignedURL (file, folder) {
        var uA = window.navigator.userAgent.toLowerCase();

        var useBlob = uA.indexOf("edge") > -1 || uA.indexOf("edg") > -1

        var fileExtension = '.' + file.name.split('.')[1]

        var prefix = file.name.split('.')[0]

        var name = prefix + Math.random().toString(36).substring(7) + new Date().getTime() + fileExtension

        var blob = file.slice(0, file.size, file.type)

        if (useBlob) {
            var newBlob = new Blob([blob], {type : file.type})
            newBlob.name = name
        } else {
            var newFile = new File([blob], name, {type: file.type})
        }

        let payload = {
            filePath: name,
            contentType: file.type
        }
        axios.post(`${process.env.VUE_APP_API_BASE_URL}/s3/signedurl`, payload)
            .then((res) => {
                var signedUrl = res.data.url 

                fetch(signedUrl, {
                        method: 'PUT',
                        body: blob,
                    }).then(resp => {
                        this.form.courtOrder.fileId = name
                })
            })
        .catch((err) => {
            console.log(err)
        })
    },

    upload() {        
        console.log("starting")
        const fd = new FormData()
        fd.append('file', this.file)
        console.log(this.file)
        console.log(fd)
          
        axios.post(`${process.env.VUE_APP_API_BASE_URL}/upload`, fd,
            {
                    headers: {
                        "Content-type": "multipart/form-data",
                    },                    
                })
          .then(response => {
              console.log(response)
              this.form.courtOrder.fileId = response.data.id
        })
    }
  },
}
</script>

<style>
    .card-header {
        font-weight: 700;
        font-size: 1.2em;
    }
</style>